<template>
  <div class="row">
    <internal-comment-block
        v-model="form.application.internal_comment"
        v-if="canShowInternalComment"
        :form="form"
    />
    <div class="col-md-4">
      <organization-subject-input
          v-model="organization.subject_id"
          :form="form"
          :errors="errors"
          :items="organizationSubjects"
          :nationalities-list="nationalitiesList"
          :organization-subjects-residency="organizationSubjectsResidency"
          :disabled="disabledSubject"
          @change="changeSubject"
      />
    </div>
    <div class="col-md-8">
      <organization-name-input
          v-model="organization.name"
          :form="form"
          :errors="errors"
          :typeahead="typeaheadOrganizationName"
          :select-typeahead="selectTypeaheadOrganization"
          :nationalities-list="nationalitiesList"
          :location-name="locationName"
          :disabled="disabledName"
          :input-label="organizationNameInputLabel"
          @switch-location-name="switchLocationName"
          @change="changeName"
          @blur="blurName($event); keyupInn($event)"
      />
    </div>
    <div class="col-md-4">
      <organization-inn-input
          v-model="organization.inn"
          :form="form"
          :errors="errors"
          :typeahead="typeaheadOrganizationInn"
          :select-typeahead="selectTypeaheadOrganization"
          :organization-subjects-residency="organizationSubjectsResidency"
          :disabled="disabledInn"
          @change="changeInn"
          @blur="blurInn($event); keyupInn($event)"
          @keyup="keyupInn"
      />
    </div>
    <div class="col-md-4">
      <organization-manager-position-input
          v-model="organization.position_of_manager"
          :form="form"
          :errors="errors"
          :prevValue="prevManagerPosition"
          :disabled="disabledPositionOfManager"
          @change="change('organization position of manager')"
          @input="change('organization position of manager')"
      />
    </div>
    <div class="col-md-4">
      <organization-manager-name-input
          v-model="organization.director_name"
          :form="form"
          :errors="errors"
          :prevValue="prevManagerName"
          :disabled="disabledDirectorName"
          @change="change('organization director name')"
          @input="change('organization director name')"
      />
    </div>
    <div class="col-md-8">
      <organization-address-input
          v-model="organization.address"
          :form="form"
          :errors="errors"
          :typeahead="typeaheadOrganizationAddress"
          :prevValue="prevAddress"
          :disabled="disabledAddress"
          @change="change('organization address')"
          @input="change('organization address')"
          ref="organizationName"
      />
    </div>
    <div class="col-md-4">
      <organization-phone-input
          v-model="organization.phone"
          :form="form"
          :errors="errors"
          :disabled="disabledPhone"
          @change="change('organization phone')"
      />
    </div>
  </div>
</template>

<script>
import applicationFormContextMixin from '@/common/mixins/applicationFormContextMixin'
import applicationFormMixin from '@/common/mixins/applicationFormMixin'
import mainMixin from '@/common/mixins/mainMixin'
import OrganizationSubjectInput from '@/common/components/application/form-inputs/organization/OrganizationSubjectInput.vue'
import OrganizationNameInput from '@/common/components/application/form-inputs/organization/OrganizationNameInput.vue'
import OrganizationInnInput from '@/common/components/application/form-inputs/organization/OrganizationInnInput.vue'
import OrganizationManagerPositionInput from '@/common/components/application/form-inputs/organization/OrganizationManagerPositionInput.vue'
import OrganizationManagerNameInput from '@/common/components/application/form-inputs/organization/OrganizationManagerNameInput.vue'
import OrganizationAddressInput from '@/common/components/application/form-inputs/organization/OrganizationAddressInput.vue'
import OrganizationPhoneInput from '@/common/components/application/form-inputs/organization/OrganizationPhoneInput.vue'
import { Logger } from '@/common/helpers/Logger'
import { ApiClient } from '@/common/helpers/api-client/ApiClient'
import { ResponseStatuses } from '@/common/enums/ResponseStatuses'
import { UserRoles } from '@/common/enums/UserRoles'
import InternalCommentBlock from '@/common/components/application/internalCommentBlock.vue';
import PermissionsEnum from '@/common/enums/PermissionsEnum'
import permissionsMixin from '@/common/mixins/PermissionsMixin'

export default {
  name: 'OrganizationFormBlock',
  mixins: [
    mainMixin,
    applicationFormMixin,
    applicationFormContextMixin,
    permissionsMixin
  ],

  components: {
    InternalCommentBlock,
    OrganizationSubjectInput,
    OrganizationNameInput,
    OrganizationInnInput,
    OrganizationManagerPositionInput,
    OrganizationManagerNameInput,
    OrganizationAddressInput,
    OrganizationPhoneInput
  },

  data: () => ({
    locationName: 'all',
    locations: [],
    prevManagerPosition: '',
    prevManagerName: '',
    prevAddress: ''
  }),

  model: {
    prop: 'organization',
    event: 'change-organization'
  },

  props: {
    organization: {
      type: Object,
      required: true
    },

    organizationSubjects: {
      type: Array,
      default: () => []
    },

    nationalitiesList: {
      default: []
    },

    organizationSubjectsResidency: {
      default: []
    }
  },

  mounted () {
    if (this.organization.inn && this.organization.inn.length > 0 && !this.isView) {
      this.keyupInn()
    }
    if (this.organization.inn && (this.isNew || this.isDuplicate)) {
      let vue = this
      if (this.form.organization.nationality_id === 182) {
        this.getOrganizationSuggestions(this.organization.inn, true, true).then(function (result) {
          if (result) {
            vue.prevManagerName = result[0].director_name ? result[0].director_name : ''
            vue.prevManagerPosition = result[0].position_of_manager ? result[0].position_of_manager : ''
            vue.prevAddress = result[0].address ? result[0].address : ''

            vue.form.organization.dadataManagerName = vue.prevManagerName
            vue.form.organization.dadataManagerPosition = vue.prevManagerPosition
          }
        });
      }
    }
  },

  computed: {
    organizationNameInputLabel () {
      return this.isEntityOrganizationSubject || this.isGkoOrganizationSubject || this.isTerminalSubject || this.isForeignSubject
        ? 'Наименование организации'
        : 'Фамилия, имя, отчество'
    },

    disabledSubject () {
      return Boolean(
        this.disabledInput ||
          (this.user && this.user.role === UserRoles.TRUSTED_USER) ||
          (this.user &&
              [
                UserRoles.SUBDIVISION,
                UserRoles.SB,
                UserRoles.SB_TRUCK,
                UserRoles.SB_OTHER
              ].includes(this.user.role) &&
              this.user.organization_id)
      )
    },

    disabledName () {
      if (this.isFizOrganizationSubject) {
        return Boolean(this.disabledInput)
      }

      return Boolean(
        this.disabledInput ||
          this.isTerminalSubject ||
          (this.user &&
              (this.user.role === UserRoles.TRUSTED_USER ||
                  ([
                    UserRoles.SUBDIVISION,
                    UserRoles.SB,
                    UserRoles.SB_TRUCK,
                    UserRoles.SB_OTHER
                  ].includes(this.user.role) &&
                      this.user.organization_id)))
      )
    },

    disabledInn () {
      if (this.isFizOrganizationSubject) {
        return Boolean(
          this.disabledInput ||
            this.isTerminalSubject ||
            (this.user &&
                (this.user.role === UserRoles.TRUSTED_USER ||
                    ([
                      UserRoles.SUBDIVISION,
                      UserRoles.SB,
                      UserRoles.SB_TRUCK,
                      UserRoles.SB_OTHER
                    ].includes(this.user.role) &&
                        this.user.organization_id)))
        )
      }

      return Boolean(
        this.disabledInput || this.isTerminalSubject || this.isForeignSubject ||
          (this.user && (this.user.role === UserRoles.TRUSTED_USER ||
              ([
                UserRoles.SUBDIVISION,
                UserRoles.SB,
                UserRoles.SB_TRUCK,
                UserRoles.SB_OTHER
              ].includes(this.user.role) &&
                  this.user.organization_id)))
      )
    },

    disabledPositionOfManager () {
      return Boolean(
        this.disabledInput ||
          this.isTerminalSubject ||
          this.isFizOrganizationSubject
      )
    },

    disabledDirectorName () {
      return Boolean(this.disabledPositionOfManager) ||
          this.isTerminalSubject
    },

    disabledAddress () {
      return Boolean(this.disabledInput) ||
          this.isTerminalSubject
    },

    disabledPhone () {
      return Boolean(this.disabledInput) ||
          this.isTerminalSubject
    },

    canShowInternalComment() {
      return this.hasPermission(PermissionsEnum.canUseInternalComment) && this.isView
    }
  },

  methods: {
    change (inputName) {
      Logger.log(`Changed ${inputName}!`)
      this.$emit('change-organization', this.organization)
    },

    changeSubject () {
      this.$emit('change-subject')
      this.prevAddress = null
      this.prevManagerPosition = null
      this.prevManagerName = null
      this.form.organization.inn = null
      this.form.organization.name = null
      this.form.organization.address = null
      this.form.organization.director_name = null
      this.form.organization.phone = ''
      this.form.organization.position_of_manager = null
      this.form.organization.innError = false

      if (this.isFizOrganizationSubject) {
        this.form.organization.inn = ''
      }

      if (this.isTerminalSubject) {
        ApiClient.getTerminalOrganization().then((terminalOrganization) => {
          this.form.organization = Object.assign(this.form.organization, terminalOrganization.data)
        })
      }
    },

    changeName ($event) {
      this.checkInnControlSum()
      this.$emit('change', $event)
    },

    blurName ($event) {
      this.checkInnControlSum()
      this.$emit('blur', $event)
    },

    changeInn ($event) {
      this.form.application.documents = []
      this.checkInnControlSum()
      this.$emit('input', $event)
    },

    blurInn ($event) {
      this.checkInnControlSum()
      this.$emit('blur', $event)
    },

    keyupInn () {
      ApiClient.checkOrganizationInn({
        inn: this.organization.inn,
        user_id: this.user.id
      }).then((response) => {
        const result = response.data

        if (result.status === ResponseStatuses.Fail) {
          this.form.organization.innError = true
          this.form.organization.innHint = result.message
        } else {
          this.form.organization.innError = false
          this.form.organization.innHint = null
        }
      })
    },

    switchLocationName () {
      this.locationName = this.locationName !== 'all' ? 'all' : this.configuration?.find_region ?? 'nw'
    },

    selectTypeaheadOrganization (item, inn = false) {
      if (this.isFizOrganizationSubject) {
        if (item instanceof Object) {
          this.organization.name = item.value
        }

        return
      }

      if (item === null) {
        if (!inn) {
          item = this.organization.data
        } else {
          item = this.organization.innData
        }
      }

      if (item instanceof Object) {
        this.organization.inn = item.inn
        this.organization.innData = item.inn
        this.organization.address = item.address ? item.address : null
        this.organization.position_of_manager = item.position_of_manager ? item.position_of_manager : null
        this.organization.director_name = item.director_name ? item.director_name : null
        this.organization.name = item.org_name
        // this.form.organization.phone = item.phones
        //   ? String(item.phones.join(', '))
        //   : ''
        this.checkVisitorOrganizationBlock()
        this.checkInnControlSum()
        this.setPrevValues();
        this.keyupInn()
      }
    },

    setPrevValues() {
      this.prevAddress = this.organization.address
      this.prevManagerName = this.organization.director_name
      this.prevManagerPosition = this.organization.position_of_manager
      this.form.organization.dadataManagerName = this.prevManagerName
      this.form.organization.dadataManagerPosition = this.prevManagerPosition
    },

    async typeaheadOrganizationAddress (query, done) {
      let entity = 'organization'
      await this.typeaheadAddresses(query, done, false, entity)
      this.resetTypeaheadActiveIndex(
        this.$refs.organizationName.$refs.addressRef
      )
    }
  }
}
</script>
